import { VonqContractPostingRequirements } from 'api-clients/monolith/models/VonqContractPostingRequirements';
import { useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { z } from 'zod';

import { shouldShowField } from '../job-details/posting-requirements/display-rules';
import { buildLoadedOptionsFromCache } from '../job-details/posting-requirements/use-autocomplete-options';

import { getPlainTextLength } from '@/utils/string-util';

import { MIN_JOB_DESCRIPTION_LENGTH } from './constants';

const JOB_TYPE_VALUES = [
  'permanent',
  'temporary',
  'contract_freelancer',
  'casual',
  'finance_plus_accounting',
  'operations_leadership',
  'all_other_administrative_plus_professional',
  'information_technology',
  'customer_service_plus_operations',
  'local',
  'regional',
  'trainee',
  'shuttle',
] as const;

export const baseFields = {
  title: z.string(),
  description: z.string(),
  industry: z.string().nullable(),
  jobHours: z.enum(['full_time', 'part_time']).nullable(),
  jobType: z.enum(JOB_TYPE_VALUES).nullable(),
  experience: z.number().nullable(),
  locationAddress: z.string().nullable(),
  postalCode: z.string().nullable(),
  city: z.string().nullable(),
  stateCode: z.string().nullish(),
  remote: z.boolean().optional(),
};

/**
 * Hook to create the validation schema for job details (title, description, states).
 *
 * Field-level always-required: `title`, `description`.
 *
 * Conditionally required (validated per-opening via `superRefine`):
 * - `stateCode`: required when the opening is remote AND ZipRecruiter is selected.
 * - Server-managed fields (`industry`, `jobHours`, `jobType`, `experience`,
 *   `locationAddress`, `postalCode`, `city`): required only when the opening is
 *   selected by at least one Vonq channel (i.e. its id appears in some
 *   `channels[k].openingBudgets` where `channels[k].vonqDetails != null`).
 * The schema is built as `z.object(vonqShape).catchall(baseSchema)` instead of
 * `z.record().superRefine()` so that Vonq-required checks fire when
 * `trigger('jobDetails.<openingId>')` validates a single opening (e.g. on Save).
 *
 * @param isZipRecruiterSelected - Passed from the form's parent so the schema can
 *   validate `stateCode` without reaching into context.
 * @param vonqSelectedOpeningIds - Set of opening ids that are present in any Vonq
 *   channel's `openingBudgets`. Drives conditional required-checks for the
 *   server-managed fields above.
 */
export function useJobDetailsSchema(
  isZipRecruiterSelected = false,
  vonqSelectedOpeningIds: ReadonlySet<string> = new Set(),
) {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const messages = {
    titleRequired: intl.formatMessage({
      defaultMessage: 'Title is required',
      description: 'Title input required error message',
    }),
    descriptionRequired: intl.formatMessage({
      defaultMessage: 'Description is required',
      description: 'Description input required error message',
    }),
    descriptionMinLength: intl.formatMessage({
      defaultMessage: 'Description must be at least 250 characters long',
      description: 'Description input validation error message',
    }),
    stateCodeRequired: intl.formatMessage({
      defaultMessage: 'Hiring State is required',
      description:
        'State code required when ZipRecruiter is selected for remote openings',
    }),
    industryRequired: intl.formatMessage({
      defaultMessage: 'Industry is required',
      description:
        'Validation error shown when the opening has no industry set',
    }),
    jobHoursRequired: intl.formatMessage({
      defaultMessage: 'Job hours is required',
      description:
        'Validation error shown when the opening has no job hours (full-time/part-time) set',
    }),
    jobTypeRequired: intl.formatMessage({
      defaultMessage: 'Job type is required',
      description:
        'Validation error shown when the opening has no job type (permanent, temporary, etc.) set',
    }),
    experienceRequired: intl.formatMessage({
      defaultMessage: 'Years of experience is required',
      description:
        'Validation error shown when the opening has no years-of-experience value set',
    }),
    locationRequired: intl.formatMessage({
      defaultMessage: 'Location is required',
      description:
        'Validation error shown when the opening has no physical location address set',
    }),
    postalCodeRequired: intl.formatMessage({
      defaultMessage: 'Postal code is required',
      description:
        'Validation error shown when the opening has no postal/ZIP code set',
    }),
    cityRequired: intl.formatMessage({
      defaultMessage: 'City is required',
      description: 'Validation error shown when the opening has no city set',
    }),
  };

  /**
   * Build a per-opening schema with a `superRefine` that handles both the
   * always-required fields (title, description) and the conditional checks
   * (stateCode for ZipRecruiter, Vonq-required server-managed fields).
   *
   * Because each Vonq opening gets its own schema instance via the
   * `z.object(vonqShape)` below, `trigger('jobDetails.<id>')` fires this
   * `superRefine` for a single opening — matching the Save-button behavior.
   */
  const vonqRequiredMessage = intl.formatMessage({
    defaultMessage: 'This field is required',
    description: 'Required field error for a VONQ posting requirement field',
  });

  const vonqMessages = {
    mustBeInteger: intl.formatMessage({
      defaultMessage: 'Must be a whole number',
      description:
        'Validation error for a VONQ posting requirement field that only accepts integers',
    }),
    mustBeFloat: intl.formatMessage({
      defaultMessage: 'Must be a valid number',
      description:
        'Validation error for a VONQ posting requirement field that only accepts numeric values',
    }),
    mustBeEmail: intl.formatMessage({
      defaultMessage: 'Must be a valid email address',
      description:
        'Validation error for a VONQ posting requirement field that only accepts email addresses',
    }),
    mustBeUrl: intl.formatMessage({
      defaultMessage: 'Must be a valid URL',
      description:
        'Validation error for a VONQ posting requirement field that only accepts URLs',
    }),
    mustBeDate: intl.formatMessage({
      defaultMessage: 'Must be a valid date',
      description:
        'Validation error for a VONQ posting requirement field that only accepts dates',
    }),
    invalidFormat: intl.formatMessage({
      defaultMessage: "Value doesn't match the required format",
      description:
        'Validation error for a VONQ posting requirement field whose value does not match the required pattern',
    }),
    minLength: (n: string) =>
      intl.formatMessage(
        {
          defaultMessage: 'Must be at least {n} characters',
          description:
            'Validation error for a VONQ posting requirement field with a minimum character length',
        },
        { n },
      ),
    maxLength: (n: string) =>
      intl.formatMessage(
        {
          defaultMessage: 'Must be no more than {n} characters',
          description:
            'Validation error for a VONQ posting requirement field with a maximum character length',
        },
        { n },
      ),
    maxItems: (n: string) =>
      intl.formatMessage(
        {
          defaultMessage: 'You can select up to {n} options',
          description:
            'Validation error for a VONQ posting requirement field with a maximum number of selectable options',
        },
        { n },
      ),
    minExceedsMax: intl.formatMessage({
      defaultMessage: 'Must be less than or equal to the maximum',
      description:
        'Validation error shown on a VONQ salary or numeric minimum field when its value is greater than the corresponding maximum field',
    }),
  };

  // Patterns used to detect min/max field name pairs (e.g. salary_min / salary_max).
  // Each entry is [minPattern, maxReplacement]. Ordered from most-specific to least
  // so that "_min" is checked before the bare "Min" camelCase pattern.
  const MIN_MAX_PAIRS: [RegExp, string][] = [
    [/_min$/, '_max'],
    [/_from$/, '_to'],
    [/minimum$/i, 'maximum'],
    [/Min$/, 'Max'],
  ];

  const makeOpeningSchema = (isVonqOpening: boolean) =>
    z
      .object({
        ...baseFields,
        // User-entered values keyed by contractId → fieldName.
        // Field values are optional at the schema level because unselected
        // SELECT/date fields store `undefined` in RHF (not an empty string).
        // Required-field enforcement is handled separately in superRefine below.
        vonqPostingRequirements: z
          .record(
            z.string(),
            z.record(
              z.string(),
              // nullish (not just optional) because cleared SELECT/DATE pickers
              // write null into RHF, which must not trigger Zod's default type
              // error — required-field enforcement is handled in superRefine.
              z.union([z.string(), z.array(z.string())]).nullish(),
            ),
          )
          .default({}),
        // Field definitions from the posting requirements API, stored in form
        // state so superRefine can validate required fields without needing
        // reactive state outside of RHF.
        vonqPostingRequirementsDefs: z
          .array(z.custom<VonqContractPostingRequirements>())
          .default([]),
      })
      .superRefine((detail, ctx) => {
        if (!detail.title.trim()) {
          ctx.addIssue({
            code: 'custom',
            message: messages.titleRequired,
            path: ['title'],
          });
        }

        if (!detail.description.trim()) {
          ctx.addIssue({
            code: 'custom',
            message: messages.descriptionRequired,
            path: ['description'],
          });
        } else if (
          getPlainTextLength(detail.description) < MIN_JOB_DESCRIPTION_LENGTH
        ) {
          ctx.addIssue({
            code: 'custom',
            message: messages.descriptionMinLength,
            path: ['description'],
          });
        }

        if (
          isZipRecruiterSelected &&
          detail.remote === true &&
          (!detail.stateCode || !String(detail.stateCode).trim())
        ) {
          ctx.addIssue({
            code: 'custom',
            message: messages.stateCodeRequired,
            path: ['stateCode'],
          });
        }

        if (!isVonqOpening) return;

        if (!detail.industry || !detail.industry.trim()) {
          ctx.addIssue({
            code: 'custom',
            message: messages.industryRequired,
            path: ['industry'],
          });
        }
        if (detail.jobHours === null) {
          ctx.addIssue({
            code: 'custom',
            message: messages.jobHoursRequired,
            path: ['jobHours'],
          });
        }
        if (detail.jobType === null) {
          ctx.addIssue({
            code: 'custom',
            message: messages.jobTypeRequired,
            path: ['jobType'],
          });
        }
        if (detail.experience === null || detail.experience < 0) {
          ctx.addIssue({
            code: 'custom',
            message: messages.experienceRequired,
            path: ['experience'],
          });
        }
        if (!detail.locationAddress || !detail.locationAddress.trim()) {
          ctx.addIssue({
            code: 'custom',
            message: messages.locationRequired,
            path: ['locationAddress'],
          });
        }
        if (!detail.postalCode || !detail.postalCode.trim()) {
          ctx.addIssue({
            code: 'custom',
            message: messages.postalCodeRequired,
            path: ['postalCode'],
          });
        }
        if (!detail.city || !detail.city.trim()) {
          ctx.addIssue({
            code: 'custom',
            message: messages.cityRequired,
            path: ['city'],
          });
        }

        // Validate VONQ posting requirement fields.
        // Definitions are stored in form state alongside values so this check
        // works purely from form data — no external ref or reactive state needed.
        for (const contract of detail.vonqPostingRequirementsDefs) {
          const loadedOptions = buildLoadedOptionsFromCache(
            contract,
            queryClient,
          );
          for (const field of contract.posting_requirements) {
            const reqValues = detail.vonqPostingRequirements as Record<
              string,
              Record<string, string | string[] | undefined>
            >;
            const contractVals = reqValues[contract.contract_id] ?? {};

            // Skip hidden fields entirely — no required error and no rule validation.
            if (
              !shouldShowField(
                field,
                contractVals,
                contract.posting_requirements,
                loadedOptions,
              )
            ) {
              continue;
            }

            const value = reqValues[contract.contract_id]?.[field.name];
            const isArray = Array.isArray(value);
            const isEmpty = isArray
              ? value.length === 0
              : !String(value ?? '').trim();
            const issuePath = [
              'vonqPostingRequirements',
              contract.contract_id,
              field.name,
            ];

            if (field.required && isEmpty) {
              ctx.addIssue({
                code: 'custom',
                message: vonqRequiredMessage,
                path: issuePath,
              });
              // Skip rule validation when the field is empty — required error
              // is sufficient and rule checks would produce redundant messages.
              continue;
            }

            // Skip rule validation for empty optional fields.
            if (isEmpty) continue;

            const strValue = isArray ? '' : String(value ?? '');

            for (const rule of field.rules ?? []) {
              switch (rule.rule) {
                case 'int':
                  if (!/^-?\d+$/.test(strValue)) {
                    ctx.addIssue({
                      code: 'custom',
                      message: vonqMessages.mustBeInteger,
                      path: issuePath,
                    });
                  }
                  break;

                case 'float':
                  // Allow optional leading minus, digits, optional comma
                  // thousands separators, and optional decimal point.
                  if (!/^-?[\d,]*\.?\d+$/.test(strValue)) {
                    ctx.addIssue({
                      code: 'custom',
                      message: vonqMessages.mustBeFloat,
                      path: issuePath,
                    });
                  }
                  break;

                case 'email':
                  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(strValue)) {
                    ctx.addIssue({
                      code: 'custom',
                      message: vonqMessages.mustBeEmail,
                      path: issuePath,
                    });
                  }
                  break;

                case 'url':
                  try {
                    new URL(strValue);
                  } catch {
                    ctx.addIssue({
                      code: 'custom',
                      message: vonqMessages.mustBeUrl,
                      path: issuePath,
                    });
                  }
                  break;

                case 'minlength':
                  if (rule.data && strValue.length < parseInt(rule.data, 10)) {
                    ctx.addIssue({
                      code: 'custom',
                      message: vonqMessages.minLength(rule.data),
                      path: issuePath,
                    });
                  }
                  break;

                case 'maxlength':
                  if (rule.data && strValue.length > parseInt(rule.data, 10)) {
                    ctx.addIssue({
                      code: 'custom',
                      message: vonqMessages.maxLength(rule.data),
                      path: issuePath,
                    });
                  }
                  break;

                case 'maxitems':
                  if (
                    isArray &&
                    rule.data &&
                    value.length > parseInt(rule.data, 10)
                  ) {
                    ctx.addIssue({
                      code: 'custom',
                      message: vonqMessages.maxItems(rule.data),
                      path: issuePath,
                    });
                  }
                  break;

                case 'regex': {
                  if (rule.data) {
                    try {
                      // data is a full delimited regex string like "/^[a-z]{2}$/i"
                      // — extract pattern and flags before constructing RegExp.
                      const match = rule.data.match(/^\/(.*)\/([a-z]*)$/s);
                      const re = match
                        ? new RegExp(match[1], match[2])
                        : new RegExp(rule.data);
                      if (!re.test(strValue)) {
                        ctx.addIssue({
                          code: 'custom',
                          message: vonqMessages.invalidFormat,
                          path: issuePath,
                        });
                      }
                    } catch {
                      // Skip unparseable regex from the API — server will validate.
                    }
                  }
                  break;
                }

                case 'date': {
                  // FormDatePicker outputs ISO strings (YYYY-MM-DD).
                  // Validate the stored value matches the format the API expects.
                  const isYmd =
                    rule.data === 'Y-m-d' &&
                    /^\d{4}-\d{2}-\d{2}$/.test(strValue);
                  const isDmY =
                    rule.data === 'd.m.Y' &&
                    /^\d{2}\.\d{2}\.\d{4}$/.test(strValue);
                  if (!isYmd && !isDmY) {
                    ctx.addIssue({
                      code: 'custom',
                      message: vonqMessages.mustBeDate,
                      path: issuePath,
                    });
                  }
                  break;
                }
              }
            }
          }

          // Cross-field validation: min must not exceed max for paired numeric fields.
          // Collects all numeric fields for the contract, then detects min/max pairs
          // by name pattern (e.g. salary_min / salary_max, salary_from / salary_to).
          const contractFieldValues = detail.vonqPostingRequirements[
            contract.contract_id
          ] as Record<string, string | string[] | undefined> | undefined;

          const numericFieldValues = new Map<string, number>();
          for (const f of contract.posting_requirements) {
            const hasNumericRule = (f.rules ?? []).some(
              r => r.rule === 'int' || r.rule === 'float',
            );
            if (!hasNumericRule) continue;
            const raw = contractFieldValues?.[f.name];
            if (typeof raw !== 'string' || !raw.trim()) continue;
            // Strip comma thousands separators before parsing (VONQ float format).
            const num = parseFloat(raw.replace(/,/g, ''));
            if (!isNaN(num)) numericFieldValues.set(f.name, num);
          }

          for (const [minFieldName, minValue] of numericFieldValues) {
            for (const [pattern, maxSuffix] of MIN_MAX_PAIRS) {
              if (!pattern.test(minFieldName)) continue;
              const maxFieldName = minFieldName.replace(pattern, maxSuffix);
              const maxValue = numericFieldValues.get(maxFieldName);
              if (maxValue === undefined) continue;
              if (minValue > maxValue) {
                ctx.addIssue({
                  code: 'custom',
                  message: vonqMessages.minExceedsMax,
                  path: [
                    'vonqPostingRequirements',
                    contract.contract_id,
                    minFieldName,
                  ],
                });
              }
              // Only match the first applicable pattern per field to avoid
              // duplicate errors when multiple patterns match the same name.
              break;
            }
          }
        }
      });

  const vonqShape: Record<string, z.ZodOptional<z.ZodType>> = {};
  for (const openingId of vonqSelectedOpeningIds) {
    vonqShape[openingId] = makeOpeningSchema(true).optional();
  }

  return z.object(vonqShape).catchall(makeOpeningSchema(false));
}
